import { useMemo, useState, type ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { errorMessage } from './errorMessage';
import { SnackbarContext, type SnackbarValue } from './SnackbarContext';

interface Toast {
    id: number;
    severity: 'error' | 'success';
    message: string;
}

let nextId = 0;

/** One snackbar for the whole app; mounted by the AppShell around the page outlet. */
export function SnackbarProvider({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const [toast, setToast] = useState<Toast | null>(null);

    const value = useMemo<SnackbarValue>(
        () => ({
            showError: (error) => {
                const message = errorMessage(error, t);
                if (message) {
                    setToast({ id: nextId++, severity: 'error', message });
                }
            },
            showSuccess: (message) => setToast({ id: nextId++, severity: 'success', message }),
        }),
        [t],
    );

    return (
        <SnackbarContext.Provider value={value}>
            {children}
            <Snackbar
                key={toast?.id}
                open={toast !== null}
                autoHideDuration={6000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={toast?.severity ?? 'info'} variant="filled" onClose={() => setToast(null)}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
}
